import React, { useState, useMemo } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconArrowRight,
  IconSearch,
  IconX
} from "@tabler/icons-react";
import {
  IconMail,
  IconPhoneCall,
  IconClock,
  IconHeadset,
  IconHelpCircle,
  IconShare2,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTwitter,
} from '@tabler/icons-react';
import { Link } from "react-router-dom";
import heroImage from "../../assets/images/faq-hero.jpg";

const faqSections = [
  {
    title: "Orders & Shipping",
    items: [
      {
        question: "How Do I Track My Order?",
        answer: `1. Log In to Your Account.\n2. Click on "My Account" in the Top Menu and Select "My Order."\n3. Find the Order You Want To Track and Click "Track Order."\n4. You'll See the Current Status, Estimated Delivery Date, and Tracking Number.`,
      },
      {
        question: "What Are The Shipping Options And Costs?",
        answer: `We offer both Standard and Express shipping. The cost depends on your location and the weight of your order. You'll see exact shipping charges at checkout.`,
      },
      {
        question: "Can I Change My Shipping Address After Placing An Order?",
        answer: `Yes, as long as your order hasn't shipped yet. You can update your address from your account page or by contacting our support team.`,
      },
      {
        question: "What Happens If My Order Is Delayed Or Lost?",
        answer: `If your order is delayed, we'll do our best to expedite it. If it's lost, we'll replace or refund your purchase after confirming with the carrier.`,
      },
    ],
  },
  {
    title: "Designing & Customization",
    items: [
      {
        question: "How Do I Upload My Own Design?",
        answer: `You can upload your own images or graphics by clicking "Upload" in our design tool. Accepted file formats are PNG, JPG, or SVG.`,
      },
      {
        question: "What File Formats Are Supported?",
        answer: `Our system supports PNG, JPG, JPEG, GIF, and SVG. For best results, use high-resolution images (300 DPI or above).`,
      },
      {
        question: "Do You Offer Free Design Templates?",
        answer: `Yes, we have a library of free design templates that you can customize. Simply choose a template and edit it with your text or images.`,
      },
      {
        question: "Can I Edit My Design After Placing An Order?",
        answer: `Unfortunately, once an order is placed, the design can't be changed. Please ensure your design is final before checkout.`,
      },
    ],
  },
  {
    title: "Payments & Refunds",
    items: [
      {
        question: "What Payment Methods Do You Accept?",
        answer: `We accept all major credit cards, PayPal, and select local payment methods depending on your region.`,
      },
      {
        question: "How Do I Apply A Promo Code?",
        answer: `Enter your promo code at checkout in the "Discount Code" field. The discount will apply if the code is valid.`,
      },
      {
        question: "Can I Cancel My Order And Get A Refund?",
        answer: `You can cancel your order for a full refund if it hasn't entered production. Once production begins, cancellations or refunds may not be available.`,
      },
    ],
  },
  {
    title: "Technical Issues",
    items: [
      {
        question: "I'm Having Trouble Uploading My Design",
        answer: `If you're having trouble uploading your design, try clearing your browser cache or using a different browser. If the issue persists, contact our support team.`,
      },
      {
        question: "Why Is My Design Blurry Or Pixelated?",
        answer: `For best results, use high-resolution images (300 DPI or above). Blurry or pixelated designs are usually due to low-quality images.`,
      },
      {
        question: "Why Is My Design Not Centered?",
        answer: `Our design tool automatically centers your design. If it's not centered, try resizing or repositioning your design within the print area.`,
      },
    ],
  },
  {
    title: "Account & Privacy",
    items: [
      {
        question: "How Do I Create An Account?",
        answer: `To create an account, click "Sign Up" in the top menu. Enter your email and password, or sign up with Google or Facebook.`,
      },
      {
        question: "How Do I Change My Password?",
        answer: `You can change your password from your account settings. Click "My Account" and select "Change Password."`,
      },
      {
        question: "Is My Personal Information Secure?",
        answer: `Yes, we take data security seriously. Your personal information is encrypted and stored securely. We never share your data with third parties.`,
      },
    ]
  }
];

export default function Faq() {
  const [openQuestion, setOpenQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleQuestion = (key) => {
    setOpenQuestion((prev) => (prev === key ? null : key));
  };

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return faqSections;

    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return faqSections.map(section => {
      const filteredItems = section.items.filter(item => {
        return (
          item.question.toLowerCase().includes(lowerCaseSearchTerm) ||
          item.answer.toLowerCase().includes(lowerCaseSearchTerm)
        );
      });

      return {
        ...section,
        items: filteredItems
      };
    }).filter(section => section.items.length > 0);
  }, [searchTerm, faqSections]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const highlightText = (text) => {
    if (!searchTerm.trim()) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
      <span key={i} className="bg-yellow-200">{part}</span> : 
      part
    );
  };

  return (
    <div>
      <div className="relative w-full h-[70vh] bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 h-full flex flex-col justify-center items-start">
          <h1 className="text-white font-bold text-3xl md:text-4xl mb-2">
            Frequently Asked Questions (FAQ)
          </h1>
          <p className="text-white text-base md:text-lg mb-6 max-w-[600px]">
            Find quick answers to common questions about ordering, designs, shipping, and more!
          </p>
          <button className="bg-[#FFD100] text-black font-semibold py-2 px-6 rounded hover:bg-[#FFC700] transition-colors">
            <Link to={"/contactus"} className="flex items-center gap-2">
              Get Help Now
            </Link>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 w-full">
          <div className="bg-[#0B0C52] mx-auto w-full px-4 pt-6 text-sm text-white">
            <nav className="align-center">
              <Link to="/" className="hover:underline">Home</Link>
              <span className="mx-2">/</span>
              <span className="font-medium text-[#FFD700]">FAQ</span>
            </nav>
          </div>
        </div>
      </div>
      
      <div className="bg-[#EEE8E8] min-h-screen py-10 px-4">
        <div className="max-w-screen-xl mx-auto">
          <h1 className="text-center text-3xl md:text-4xl font-bold mb-8 text-[#00148c]">
            Help is here
          </h1>

          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-12 flex relative">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Type Your Question Here..."
                className="w-full border border-[#00148c] text-[#00148c] px-4 py-2 rounded-l-md focus:outline-none pr-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#00148c]"
                >
                  <IconX size={18} />
                </button>
              )}
            </div>
            <button 
              type="submit"
              className="bg-[#1F0B29] text-white px-5 py-2 rounded-r-md hover:bg-[#11083B] transition-colors flex items-center justify-center"
            >
              <IconSearch size={20} />
            </button>
          </form>

          {searchTerm && filteredSections.length === 0 && (
            <div className="text-center text-[#1F0B29] mb-12">
              <p className="text-lg">No results found for "{searchTerm}"</p>
              <button 
                onClick={clearSearch}
                className="mt-2 text-[#364add] hover:underline"
              >
                Clear search
              </button>
            </div>
          )}

          {filteredSections.map((section, sIndex) => (
            <div key={sIndex} className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#00148c]">
                {section.title}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {section.items.map((item, qIndex) => {
                  const questionKey = `${sIndex}-${qIndex}`;
                  const isOpen = openQuestion === questionKey;

                  return (
                    <div
                      key={questionKey}
                      className="bg-[#EEE8E8] border border-[#00148c] rounded-md p-4"
                    >
                      <button
                        type="button"
                        onClick={() => toggleQuestion(questionKey)}
                        className="w-full flex items-center justify-between focus:outline-none"
                      >
                        <span className="font-semibold text-[#1F0B29] text-left mr-2">
                          {highlightText(item.question)}
                        </span>
                        {isOpen ? (
                          <IconChevronUp size={18} className="text-[#1F0B29]" />
                        ) : (
                          <IconChevronDown size={18} className="text-[#1F0B29]" />
                        )}
                      </button>

                      {isOpen && (
                        <div className="mt-3 text-[#1F0B29] whitespace-pre-line text-sm leading-relaxed">
                          {highlightText(item.answer)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#EEE8E8] py-10 px-4">
          <div className="max-w-screen-xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-8">
              Ways to Reach Us
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
                <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                  <IconHeadset size={24} />
                  <h3 className="text-lg font-bold">Customer Support</h3>
                </div>
                <div className="flex items-center gap-2 mb-2 text-[#1F0B29]">
                  <IconMail size={16} />
                  <p>support@company.com</p>
                </div>
                <div className="flex items-center gap-2 mb-2 text-[#1F0B29]">
                  <IconPhoneCall size={16} />
                  <p>+1-800-555-1234</p>
                </div>
                <div className="flex items-center gap-2 text-[#1F0B29]">
                  <IconClock size={16} />
                  <p>Monday - Friday | 9 AM - 6 PM EST</p>
                </div>
              </div>

              <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
                <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                  <IconHelpCircle size={24} />
                  <h3 className="text-lg font-bold">FAQ &amp; Help Center</h3>
                </div>
                <p className="text-[#1F0B29] mb-4">
                  Find answers to common questions in our{" "}
                  <a
                    href="/help-center"
                    className="underline text-[#364add] hover:text-[#11083B]"
                  >
                    Help Center
                  </a>
                  .
                </p>
                <button className="inline-flex items-center gap-2 bg-[#1A237E] text-white px-4 py-2 rounded transition-colors w-full justify-center">
                  <IconHelpCircle size={16} />
                  <span>Visit FAQ</span>
                </button>
              </div>

              <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
                <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                  <IconShare2 size={24} />
                  <h3 className="text-lg font-bold">Social Media Support</h3>
                </div>
                <p className="text-[#1F0B29] mb-4">
                  Reach out on our official social media channels
                </p>
                <div className="flex justify-center items-center gap-4 text-[#1F0B29]">
                  <a
                    href="https://facebook.com"
                    aria-label="Facebook"
                    className="hover:text-[#4267B2]"
                  >
                    <IconBrandFacebook size={24} />
                  </a>
                  <a
                    href="https://twitter.com"
                    aria-label="Twitter"
                    className="hover:text-[#1DA1F2]"
                  >
                    <IconBrandTwitter size={24} />
                  </a>
                  <a
                    href="https://instagram.com"
                    aria-label="Instagram"
                    className="hover:text-pink-600"
                  >
                    <IconBrandInstagram size={24} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}